import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  completeTraining,
  createTrainingProfile,
  getMyTrainingAssignments,
  getMyTrainingSummary,
  getTrainingAssignment,
  getTrainingCompliance,
  getTrainingDirectory,
  getTrainingProfiles,
  getTrainingSupervisorAlerts,
  saveTrainingProgress,
  submitTrainingQuiz,
  updateTrainingProfile,
} from '../api/trainingApi';
import type {
  CompleteTrainingInput,
  TrainingFilters,
  TrainingProfileInput,
} from '../types/training.types';

export const TRAINING_QUERY_KEYS = {
  root: ['eqms-training'] as const,
  summary: ['eqms-training', 'summary'] as const,
  assignments: (filters: TrainingFilters) => ['eqms-training', 'assignments', filters] as const,
  assignment: (id: string | null) => ['eqms-training', 'assignment', id] as const,
  compliance: (filters: TrainingFilters) => ['eqms-training', 'compliance', filters] as const,
  profiles: ['eqms-training', 'profiles'] as const,
  directory: ['eqms-training', 'directory'] as const,
  alerts: ['eqms-training', 'alerts'] as const,
};

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Training Management request failed';
}

export function useMyTrainingSummary() {
  return useQuery({ queryKey: TRAINING_QUERY_KEYS.summary, queryFn: getMyTrainingSummary });
}

export function useMyTrainingAssignments(filters: TrainingFilters) {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.assignments(filters),
    queryFn: () => getMyTrainingAssignments(filters),
  });
}

export function useTrainingAssignment(id: string | null) {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.assignment(id),
    queryFn: () => getTrainingAssignment(id!),
    enabled: Boolean(id),
  });
}

export function useTrainingCompliance(filters: TrainingFilters, enabled: boolean) {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.compliance(filters),
    queryFn: () => getTrainingCompliance(filters),
    enabled,
  });
}

export function useTrainingProfiles(enabled: boolean) {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.profiles,
    queryFn: getTrainingProfiles,
    enabled,
  });
}

export function useTrainingDirectory(enabled: boolean) {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.directory,
    queryFn: getTrainingDirectory,
    enabled,
  });
}

export function useTrainingSupervisorAlerts() {
  return useQuery({
    queryKey: TRAINING_QUERY_KEYS.alerts,
    queryFn: getTrainingSupervisorAlerts,
  });
}

export function useSaveTrainingProgress(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => saveTrainingProgress(id),
    onSuccess: () => {
      toast.success('Reading progress saved');
      void queryClient.invalidateQueries({ queryKey: TRAINING_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useSubmitTrainingQuiz(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (answers: Record<string, string>) => submitTrainingQuiz(id, answers),
    onSuccess: (result) => {
      if (result.attempt.passed) toast.success(`Assessment passed with ${result.attempt.score}%`);
      else if (result.assignment.status === 'locked') toast.error('Assessment locked after the final attempt');
      else toast.error(`Assessment score: ${result.attempt.score}%. Try again.`);
      void queryClient.invalidateQueries({ queryKey: TRAINING_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useCompleteTraining(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CompleteTrainingInput) => completeTraining(id, input),
    onSuccess: () => {
      toast.success('Training completed and qualification recorded');
      void queryClient.invalidateQueries({ queryKey: TRAINING_QUERY_KEYS.root });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function useSaveTrainingProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: TrainingProfileInput }) =>
      id ? updateTrainingProfile(id, input) : createTrainingProfile(input),
    onSuccess: (_, variables) => {
      toast.success(variables.id ? 'Training matrix updated' : 'Training matrix created');
      void queryClient.invalidateQueries({ queryKey: TRAINING_QUERY_KEYS.profiles });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}
